const firstPresent = (...values) => values.find(
  (value) => value !== undefined && value !== null && value !== ''
);

const labelOf = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (Array.isArray(value)) return '';
  return String(firstPresent(
    value.incident_type,
    value.event,
    value.trigger,
    value.display_name,
    value.display_label,
    value.label,
    value.name,
    value.title,
    value.value
  ) || '').trim();
};

const labelsOf = (value) => {
  const source = Array.isArray(value) ? value : [value];
  return [...new Set(source.flatMap((entry) => {
    if (Array.isArray(entry)) return labelsOf(entry);
    const label = labelOf(entry);
    return label ? [label] : [];
  }))];
};

const classificationScope = (record = {}) =>
  record.foresight_classification ||
  record.classification ||
  record.classification_fields ||
  record.incident_classification ||
  record.detected_classification ||
  record.source_classification ||
  record.source_evidence?.classification ||
  {};

export const classificationPathsOf = (record = {}) => {
  const scope = classificationScope(record);
  const incidentType = labelOf(firstPresent(
    scope.incident_type,
    scope.type_name,
    scope.incident_type_name,
    scope.incident_type_label,
    record.incident_type,
    record.type_name,
    record.incident_type_name,
    record.incident_type_label
  ));
  const pairs = firstPresent(
    scope.event_triggers,
    record.event_triggers
  );

  if (Array.isArray(pairs) && pairs.length > 0) {
    return pairs.flatMap((pair) => {
      const event = labelOf(firstPresent(pair?.event, pair?.event_name, pair?.name));
      const triggers = labelsOf(firstPresent(
        pair?.triggers,
        pair?.trigger,
        pair?.trigger_name
      ));
      if (triggers.length === 0) {
        return [{ incidentType, event, trigger: '' }];
      }
      return triggers.map((trigger) => ({ incidentType, event, trigger }));
    });
  }

  const events = labelsOf(firstPresent(
    scope.event,
    scope.event_name,
    scope.event_label,
    record.event,
    record.event_name,
    record.event_label
  ));
  const triggers = labelsOf(firstPresent(
    scope.trigger,
    scope.trigger_name,
    scope.trigger_label,
    scope.alert_trigger_value,
    record.trigger,
    record.trigger_name,
    record.trigger_label,
    record.alert_trigger_value
  ));
  const event = events[0] || '';
  if (triggers.length > 0) {
    return triggers.map((trigger) => ({ incidentType, event, trigger }));
  }
  return [{ incidentType, event, trigger: '' }];
};

const looksLikeOpaqueId = (value) =>
  /^[0-9a-f]{8}-[0-9a-f-]{16,}$/i.test(value) ||
  /^[0-9a-f]{24}$/i.test(value);

export const writtenLocationOf = (record = {}) => {
  const scope = classificationScope(record);
  const location = record.location && !Array.isArray(record.location)
    ? record.location
    : {};
  const sourceEvidence = record.source_evidence || record.foresight_context || {};
  const resolution = (
    record.location_resolution ||
    location.location_resolution ||
    location.resolution ||
    sourceEvidence.location_resolution ||
    {}
  );
  const candidates = [
    record.foresight_location_text,
    record.written_location,
    record.detected_location,
    record.event_location,
    record.location_text,
    record.display_location,
    record.location_label,
    record.detected_news_location,
    scope.foresight_location_text,
    scope.written_location,
    scope.location_text,
    location.written_location,
    location.display_name,
    location.display_label,
    location.resolved_name,
    location.name,
    sourceEvidence.written_location,
    sourceEvidence.location_text,
    sourceEvidence.nearest_zone,
    record.foresight_metadata?.written_location,
    record.foresight_metadata?.location_text,
    resolution.written_location,
    resolution.resolved_name,
    resolution.display_name,
    ...(Array.isArray(record.foresight_locations) ? record.foresight_locations : []),
    ...(Array.isArray(scope.foresight_locations) ? scope.foresight_locations : []),
    ...(Array.isArray(record.location_names) ? record.location_names : []),
    ...(Array.isArray(location.affected_areas) ? location.affected_areas : []),
    record.nearest_zone,
  ];
  for (const candidate of candidates) {
    const value = labelOf(candidate);
    if (value && !looksLikeOpaqueId(value)) return value;
  }
  if (typeof record.location === 'string' && !looksLikeOpaqueId(record.location)) {
    return record.location.trim();
  }
  return '';
};

export const isForesightClassification = (record = {}) => {
  const origin = String(firstPresent(
    record.source,
    record.origin,
    record.created_by,
    record.created_by_name,
    record.generated_by,
    record.provenance?.source,
    record.source_metadata?.source,
    record.foresight_metadata?.source
  ) || '').toLowerCase();
  return Boolean(
    record.source_warning_id ||
    (Array.isArray(record.source_warning_ids) && record.source_warning_ids.length > 0) ||
    record.foresight_warning_id ||
    record.detected_hazard_id ||
    record.foresight_metadata?.warning_id ||
    record.source_metadata?.warning_id ||
    record.foresight_generated === true ||
    record.foresight_enriched === true ||
    record.foresight_created === true ||
    record.created_from_foresight === true ||
    origin === 'foresight' ||
    origin.includes('foresight')
  );
};

export const primaryClassificationOf = (record = {}) =>
  classificationPathsOf(record)[0] || { incidentType: '', event: '', trigger: '' };

export const normalizeScenarioBuilderPrefill = (prefill = {}) => {
  const scope = classificationScope(prefill);
  const primary = primaryClassificationOf(prefill);
  const foresightPrefill = isForesightClassification(prefill);
  const location = firstPresent(
    prefill.location,
    scope.location,
    prefill.location_ids
  );
  const locationNames = labelsOf(firstPresent(
    prefill.location_names,
    scope.location_names,
    writtenLocationOf(prefill)
  ));
  return {
    ...prefill,
    name: String(firstPresent(prefill.name, prefill.normalized_title) || ''),
    description: String(firstPresent(prefill.description, prefill.source_evidence?.description) || ''),
    level: String(firstPresent(prefill.level, prefill.scenario_level, scope.level) || 'Tactical'),
    type_id: String(firstPresent(
      prefill.type_id,
      prefill.incident_type_id,
      scope.type_id,
      scope.incident_type_id
    ) || ''),
    event_id: String(firstPresent(prefill.event_id, scope.event_id) || ''),
    trigger_id: String(firstPresent(prefill.trigger_id, scope.trigger_id) || ''),
    incident_type: primary.incidentType,
    event: primary.event,
    trigger: primary.trigger,
    exercise_classification: String(firstPresent(
      prefill.exercise_classification,
      prefill.exerciseClassification,
      scope.exercise_classification,
      scope.exerciseClassification,
      foresightPrefill ? 'Tabletop' : ''
    ) || ''),
    location: Array.isArray(location) ? location : (location ? [location] : []),
    location_names: locationNames,
    is_unknown_location: prefill.is_unknown_location ??
      !(Array.isArray(location) ? location.length > 0 : Boolean(location)),
  };
};
